/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "../loading_spinner";
import { ConfirmationDialog } from "../confirmation_dialog";
import { StyledAddButton, StyledCancelButton } from "../../pages/restaurant_groups/styles";
import { Query_Keys } from "../../lib/queryKeys";
import { CACHE_DURATION } from "../../constants/cache_duration";
import { PerPage } from "../../constants/perPage";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { useDebounce } from "../../utils/useDebounce";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { savedReportQueryMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/savedReportQueryMutationKeys";
import { deleteSavedReportQuery, fetchSavedReportQueries } from "../../api/savedReportQueryServices";
import {
  cancelActionStyles,
  dialogActionsStyles,
  dialogTitleStyles,
  emptyStateStyles,
  primaryActionStyles,
  queryDescriptionStyles,
  queryListStyles,
  queryNameStyles,
  queryRowStyles,
} from "./styles";

export const RESTRICTED_QUERY_HINT = "Some saved filters are no longer available to you";

const EMPTY_QUERIES = [];

export const SavedQueriesDialog = ({ open, onClose, reportType, contextId, onApply, onDeleted }) => {
  const [search, setSearch] = useState("");
  const [queryToDelete, setQueryToDelete] = useState(null);
  const debouncedSearch = useDebounce(search.trim(), 300);

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const { data, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, isError } = useInfiniteQuery({
    queryKey: Query_Keys.saved_report_queries({ reportType, contextId, search: debouncedSearch }),
    queryFn: ({ pageParam }) =>
      fetchSavedReportQueries({
        reportType,
        contextId,
        search: debouncedSearch,
        page: pageParam,
        perPage: PerPage,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.data.length, 0);
      return loaded < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    enabled: open,
    placeholderData: keepPreviousData,
    staleTime: CACHE_DURATION,
  });

  const queries = data ? data.pages.flatMap((page) => page.data) : EMPTY_QUERIES;

  const { rootRef: listRef, targetRef: loaderRef } = useInfiniteScrollObserver({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const deleteMutation = useOtMutation({
    mutationFn: (query) => deleteSavedReportQuery(query.id),
    invalidateKeys: savedReportQueryMutationKeyGroups.remove,
    successMessage: ({ data: response }) => response?.message || "Query deleted successfully",
    onSuccess: (_response, query) => {
      setQueryToDelete(null);
      onDeleted(query);
    },
  });

  const renderList = () => {
    // The search bar's spinner shows the first load; nothing is listed until it returns.
    if (isLoading) return null;
    if (isError) {
      return <Typography sx={emptyStateStyles}>Couldn't load saved queries. Please try again.</Typography>;
    }
    if (queries.length === 0) {
      return (
        <Typography sx={emptyStateStyles} data-testid="saved-queries-empty">
          {debouncedSearch
            ? `No saved queries match '${debouncedSearch}'.`
            : "No saved queries for this report yet. Set your filters and choose Save Query."}
        </Typography>
      );
    }

    return (
      <Box ref={listRef} sx={queryListStyles} data-testid="saved-queries-list">
        {queries.map((query) => (
          <Box key={query.id} sx={queryRowStyles} data-testid={`saved-query-${query.id}`}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={queryNameStyles}>{query.name}</Typography>
                {query.filters_restricted ? (
                  <Tooltip title={RESTRICTED_QUERY_HINT}>
                    <WarningAmberIcon
                      sx={{ color: "#ed6c02", fontSize: "18px" }}
                      aria-label={RESTRICTED_QUERY_HINT}
                      data-testid={`saved-query-${query.id}-restricted`}
                    />
                  </Tooltip>
                ) : null}
              </Box>
              {query.description ? (
                <Typography sx={queryDescriptionStyles}>{query.description}</Typography>
              ) : null}
            </Box>
            <StyledAddButton
              variant="contained"
              size="small"
              sx={primaryActionStyles}
              onClick={() => onApply(query)}
              data-testid={`saved-query-${query.id}-apply`}
            >
              Apply
            </StyledAddButton>
            <IconButton
              size="small"
              aria-label={`Delete ${query.name}`}
              onClick={() => setQueryToDelete(query)}
              data-testid={`saved-query-${query.id}-delete`}
            >
              <DeleteOutlineIcon sx={{ color: "#d32f2f" }} />
            </IconButton>
          </Box>
        ))}
        <DropdownInfiniteLoader hasNextPage={hasNextPage} targetRef={loaderRef} />
      </Box>
    );
  };

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        data-testid="saved-queries-dialog"
        aria-labelledby="saved-queries-dialog-title"
      >
        <DialogTitle id="saved-queries-dialog-title" sx={dialogTitleStyles}>
          Saved Queries
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search saved queries"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" sx={{ justifyContent: "center", minWidth: "20px" }}>
                  <Box
                    sx={{ alignItems: "center", display: "flex", height: "20px", justifyContent: "center", width: "20px" }}
                    data-testid="saved-queries-search-spinner"
                    data-loading={String(isFetching)}
                  >
                    <LoadingSpinner isLoading={isFetching} />
                  </Box>
                </InputAdornment>
              ),
            }}
            inputProps={{ "data-testid": "saved-queries-search", "aria-label": "Search saved queries" }}
            sx={{ mt: 1 }}
          />
          {renderList()}
        </DialogContent>
        <DialogActions sx={dialogActionsStyles}>
          <StyledCancelButton
            onClick={onClose}
            variant="outlined"
            sx={cancelActionStyles}
            data-testid="saved-queries-close"
          >
            Close
          </StyledCancelButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(queryToDelete)}
        onClose={() => setQueryToDelete(null)}
        Icon={DeleteOutlineIcon}
        dialogTitle="Delete saved query?"
        dialogBody={`'${queryToDelete?.name || ""}' will be deleted. This can't be undone.`}
        confirmationText="Yes, I want to delete this query"
        handleConfirm={() => deleteMutation.mutate(queryToDelete)}
        isLoading={deleteMutation.isPending}
        dataTestId="saved-query-delete-dialog"
      />
    </React.Fragment>
  );
};
